import clsx from 'clsx';
import type { ComponentPropsWithRef } from 'react';

import { type ButtonProps, getButtonDefaultIcon } from '.';
import Icon from '../Icon';
import styles from './index.module.scss';

// Type definition of the Button props extended with the HTML button element props.

type Props = ButtonProps & ComponentPropsWithRef<'button'>;

/**
 * Button component.
 */

const Button = (props: Props) => {
  const {
    children,
    variant,
    activeColor,
    xl,
    active,
    disabled,
    unstyled,
    className,
    style,
    ref,
    ...rest
  } = props;

  const classes = [
    styles.button,
    styles[`variant-${variant}`],
    styles[`active-${activeColor}`],
    xl && styles.xl,
    active && styles.active,
    active && 'active',
    disabled && 'disabled',
    unstyled && 'unstyled',
    className,
  ];

  // If the button variant supports an icon, conditionally render it.
  let renderedIcon = null;

  if (props.variant !== 'secondary') {
    const icon = props.icon || getButtonDefaultIcon(variant);

    if (icon !== 'none') {
      classes.push(styles[`icon-${icon}`]);
      renderedIcon = <Icon type={icon} className={styles.icon} />;
    }
  }

  // Remove the icon property from `rest` if present to avoid spreading it.
  if ('icon' in rest) delete rest.icon;

  return (
    <button ref={ref} disabled={disabled} className={clsx(classes)} style={style} {...rest}>
      {renderedIcon}
      <span className={styles.content}>{children}</span>
    </button>
  );
};

export default Button;
